import { StrictMode, useCallback, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { User } from "oidc-client-ts";
import { currentUser } from "./auth/oidc";
import { AuthCallback } from "./views/AuthCallback";
import { Chat } from "./views/Chat";
import { Login } from "./views/Login";
import "./styles.css";

const queryClient = new QueryClient();

function clearAuthStorage() {
  for (const storage of [window.sessionStorage, window.localStorage]) {
    for (const key of Object.keys(storage)) {
      if (key.startsWith("oidc.")) {
        storage.removeItem(key);
      }
    }
  }
}

function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const path = window.location.pathname;

  useEffect(() => {
    if (path === "/auth/callback") {
      return;
    }

    currentUser().then(setUser).catch(() => setUser(null));
  }, [path]);

  const handleSignedOut = useCallback(() => {
    queryClient.clear();
    clearAuthStorage();
    setUser(null);
    window.history.replaceState(null, "", "/login");
  }, []);

  if (path === "/auth/callback") {
    return <AuthCallback onSignedIn={setUser} />;
  }

  if (user === undefined) {
    return (
      <main className="centered-screen">
        <section className="status-panel" aria-live="polite">
          <div className="spinner" aria-hidden="true" />
          <p className="eyebrow">EvoBuddy</p>
          <h1>Preparing tenant portal</h1>
          <p className="muted">Checking your browser session.</p>
        </section>
      </main>
    );
  }

  if (!user) {
    if (path !== "/login") {
      window.history.replaceState(null, "", "/login");
    }
    return <Login />;
  }

  if (path === "/" || path === "/login") {
    window.history.replaceState(null, "", "/chat");
  }

  return <Chat user={user} onSignedOut={handleSignedOut} />;
}

createRoot(document.getElementById("root") as HTMLElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
